import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CompanyInfo, CustomSubscriptionForm } from "Bands/components";
import {
  BAND_ACTIONS,
  IBandCustomSubscriptionRequest,
  ICustomSubscriptionParams,
  SubscriptionType,
} from "Bands/types";
import {
  clearActionMeta,
  createCustomSubscription,
  dataMetaSelector,
  dispatchGetBandAction,
  disptachGetCustomSubscriptionRequests,
} from "Bands/state";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { Modal } from "shared/components";
import { Wallet } from "shared/assets/icons";
import { dateStringToISO, isoToUTCString, prefixWebsite } from "shared/utils";
import { API_ENDPOINTS } from "shared/constants";
import { useClearActionMetaOnUnMount, useSubscriptionError, useFetchCompany } from "Bands/utils";
import { Spacer } from "shared/layout";

const { adminDashboard } = copy;
const {
  customSubscriptions: { create: createCopy },
} = adminDashboard;

interface Props {
  handleClose: () => void;
  subscription: IBandCustomSubscriptionRequest;
}

export const CreateCustomSubscriptionModal: FC<Props> = ({
  handleClose,
  subscription: {
    id,
    company: { id: companyPath = "" },
    bands,
  },
}) => {
  const dispatch = useDispatch();

  const { company } = useFetchCompany(companyPath);

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.createCustomSubscription));

  useClearActionMetaOnUnMount(BAND_ACTIONS.createCustomSubscription);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createCustomSubscription));

      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: createCopy.success,
        })
      );

      // refetch custom subscriptions
      disptachGetCustomSubscriptionRequests({ dispatch });

      // refetch band to update subscription
      if (bands[0]?.id) {
        dispatchGetBandAction(dispatch, bands[0].id);
      }
    }
  }, [apiMeta, bands, dispatch, handleClose]);

  const handleSubmit = (subscription: ICustomSubscriptionParams) => {
    const { invoiceDate, start, stop, invoiceUrl, ...rest } = subscription;

    const cleanSubscription = {
      ...rest,
      start: start ? isoToUTCString({ iso: dateStringToISO(start), type: "start" }) : undefined,
      stop: stop ? isoToUTCString({ iso: dateStringToISO(stop), type: "end" }) : undefined,
      invoiceDate: dateStringToISO(invoiceDate || ""),
      invoiceUrl: invoiceUrl ? prefixWebsite(invoiceUrl) : undefined,
      plan: SubscriptionType.custom,
      subscriptionRequest: API_ENDPOINTS.CUSTOM_SUBSCRIPTION_REQUEST(id),
    } as ICustomSubscriptionParams;

    dispatch(createCustomSubscription(cleanSubscription));
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          children: copy.actions.add,
          "data-testid": "create_custom_subscription",
        },
        secondary: {
          onClick: handleClose,
          "data-testid": "cancel_custom_subscription",
        },
      } as IFormButtons),
    [handleClose]
  );

  const { error } = useSubscriptionError(apiMeta?.error);

  return (
    <Modal icon={Wallet} title={createCopy.title}>
      <Spacer size="l">
        {company && <CompanyInfo company={company} cardTheme={ICardTheme.secondary} />}
        <CustomSubscriptionForm
          error={error}
          isSubmitting={apiMeta?.status === IAPIState.pending}
          handleSubmit={handleSubmit}
          buttons={buttons}
          apiError={apiMeta?.error}
        />
      </Spacer>
    </Modal>
  );
};
